<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <h4 qid="projects-page-title">
      {{ $t('tasks.title') }}
    </h4>

    <b-tabs class="mt-3">

      <!-- Open tasks tab -->
      <b-tab :title="$t('projects.tab.open_tasks')" active @click="openTasksTabClicked">
        <div v-for="project in getMyProjects" class="mt-4">
          <div class="row" v-if="project.open_tasks && project.open_tasks.length">
            <div class="col-sm-5">
              <h5>
                {{ project.title }}
              </h5>
            </div>
            <div class="col-sm-7 text-right">
              <span class="mr-2" v-if="project.end_date">
                {{ $t('projects.table.due_date') }}:
              </span>
              <span class="mr-4 badge badge-success">{{ project.end_date | formatDate }}</span>

              <span class="mr-2">{{ $t('projects.table.type') }}:</span>
              <span v-if="project.type == 'update'" class="badge badge-info mr-4">
                {{ $t('projects.view.update_label') }}
              </span>
              <span v-if="project.type == 'validation'" class="badge badge-info mr-4">
                {{ $t('projects.view.validation_label') }}
              </span>
              <span v-if="project.type == 'control'" class="badge badge-info mr-4">
                {{ $t('projects.view.control_label') }}
              </span>
              <span v-if="project.type == 'security_measure'" class="badge badge-info mr-4">
                {{ $t('projects.view.security_measure_label') }}
              </span>
              <span v-if="project.type == 'risk_assessment'" class="badge badge-info mr-4">
                {{ $t('maintenance.assessments.type.' + $options.filters.slugify(project.assessment.type)) }}
              </span>

              <span v-if="project.type != 'security_measure' && project.type != 'risk_assessment'">
                <span class="mr-2">{{ $t('projects.table.scope') }}:</span>
                <span class="badge badge-info mr-4" v-if="project.scope == 'system'">
                  {{ $t('projects.system.label') }}
                </span>
                <span class="badge badge-info mr-4" v-if="project.scope == 'process'">
                  {{ $t('projects.process.label') }}
                </span>
                <span class="badge badge-info mr-4" v-if="project.scope == 'activity'">
                  {{ $t('projects.activity.label') }}
                </span>
                <span class="badge badge-info mr-4" v-if="project.scope == 'third_party'">
                  {{ $t('projects.third_party.label') }}
                </span>
              </span>

              <span v-if="project.assessment && project.type == 'risk_assessment'">
                <span class="mr-2">
                  {{ $t('table.template') }}:
                </span>
                <span class="badge badge-info mr-4" v-if="project.type == 'risk_assessment'">
                  {{ project.assessment.name }}
                </span>
              </span>

            </div>
          </div>
          <div class="card" v-if="project.open_tasks && project.open_tasks.length">
            <table class="table table-responsive-md" v-if="project.type && (project.type == 'update' || project.type == 'validation')">
              <thead>
                <tr>
                  <th scope="col">
                    {{ $t('projects.table.content') }}
                  </th>
                  <th scope="col">
                    {{ $t('projects.table.name') }}
                  </th>
                  <th scope="col">
                    {{ $t('projects.table.received_date') }}
                  </th>
                  <th>
                    {{ $t('projects.table.days_left') }}
                  </th>
                  <th width="250"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="object in project.open_tasks" v-if="project.open_tasks && project.open_tasks.length">
                  <td>
                    <span v-if="object.model">
                      <font-awesome-icon icon="desktop" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\System\\Models\\System'"/>
                      <font-awesome-icon icon="cubes" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\Activity\\Models\\Activity'"/>
                      <font-awesome-icon icon="cube" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\Process\\Models\\Process'"/>
                      <font-awesome-icon icon="file-signature" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\Maintenance\\ThirdParty\\Models\\ThirdParty'"/>
                      <font-awesome-icon icon="file-invoice" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\Maintenance\\Policy\\Models\\Policy'"/>
                      {{ object.model.name }}
                    </span>
                  </td>
                  <td>
                    <span>
                      {{ $t('tasks.values.' + $options.filters.slugify(object.title)) }}
                    </span>
                  </td>
                  <td>
                    {{ object.start_date | formatDate }}
                  </td>
                  <td>
                    <span class="badge badge-info pl-3 pr-3" v-if="object.days_left >= 0">
                      {{ object.days_left }} {{ $t('system.days') }}
                    </span>
                    <span class="badge badge-danger pl-3 pr-3" v-if="object.days_left < 0">
                      {{ object.days_left }} {{ $t('system.days') }}
                    </span>
                  </td>
                  <td>
                    <UpdateTaskModal
                        :task="object"
                        @reloadContent="loadOpenTasks"
                        :tasks="project.open_tasks"
                        v-if="project.type == 'update'"
                    ></UpdateTaskModal>
                    <ValidationTaskModal
                        :task="object"
                        @reloadContent="loadOpenTasks"
                        :tasks="project.open_tasks"
                        v-if="project.type == 'validation'"
                    ></ValidationTaskModal>
                    <b-button class="mr-2 small"
                              variant="light"
                              @click="showEditUsersModal(object)"
                              qid="delete-s-cancel-button"
                    >
                      {{ $t('tasks.re_assign_button') }}
                    </b-button>
                  </td>
                </tr>
                <tr v-if="!loadingOpen && project.open_tasks && !project.open_tasks.length" qid="no-results-row" class="text-center">
                  <td colspan="10">
                    <h5 class="mt-3">
                      {{ $t('system.no_results') }}
                    </h5>
                  </td>
                </tr>
              </tbody>
            </table>
            <table class="table table-responsive-md" v-if="project.type && project.type == 'control'">
              <thead>
                <tr>
                  <th scope="col">
                    {{ $t('projects.table.scope') }}
                  </th>
                  <th scope="col">
                    {{ $t('projects.table.content') }}
                  </th>
                  <th scope="col">
                    {{ $t('projects.table.received_date') }}
                  </th>
                  <th>
                    {{ $t('projects.table.days_left') }}
                  </th>
                  <th width="250"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="object in project.open_tasks" v-if="project.open_tasks && project.open_tasks.length">
                  <td>
                    <span v-if="object.scope">
                      <font-awesome-icon icon="shield-alt" size="lg" class="mr-1"/>
                      {{ object.scope.name }}
                    </span>
                  </td>
                  <td>
                    <span v-if="object.model">
                      <font-awesome-icon icon="desktop" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\System\\Models\\System'"/>
                      <font-awesome-icon icon="cubes" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\Activity\\Models\\Activity'"/>
                      <font-awesome-icon icon="cube" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\Process\\Models\\Process'"/>
                      <font-awesome-icon icon="file-signature" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\Maintenance\\ThirdParty\\Models\\ThirdParty'"/>
                      {{ object.model.name }}
                    </span>
                  </td>
                  <td>
                    {{ object.start_date | formatDate }}
                  </td>
                  <td>
                    <span class="badge badge-info pl-3 pr-3" v-if="object.days_left >= 0">
                      {{ object.days_left }} {{ $t('system.days') }}
                    </span>
                    <span class="badge badge-danger pl-3 pr-3" v-if="object.days_left < 0">
                      {{ object.days_left }} {{ $t('system.days') }}
                    </span>
                  </td>
                  <td>
                    <RiskTaskModal :task="object" @reloadContent="loadOpenTasks" :tasks="project.open_tasks"></RiskTaskModal>
                    <b-button class="mr-2 small"
                              variant="light"
                              @click="showEditUsersModal(object)"
                              qid="delete-s-cancel-button"
                    >
                      {{ $t('tasks.re_assign_button') }}
                    </b-button>
                  </td>
                </tr>
                <tr v-if="!loadingOpen && project.open_tasks && !project.open_tasks.length" qid="no-results-row" class="text-center">
                  <td colspan="10">
                    <h5 class="mt-3">
                      {{ $t('system.no_results') }}
                    </h5>
                  </td>
                </tr>
              </tbody>
            </table>

            <table class="table table-responsive-md" v-if="project.type && project.type == 'security_measure'">
              <thead>
                <tr>
                  <th scope="col">
                    {{ $t('projects.table.scope') }}
                  </th>
                  <th scope="col">
                    {{ $t('projects.table.received_date') }}
                  </th>
                  <th>
                    {{ $t('projects.table.days_left') }}
                  </th>
                  <th width="250"></th>
                </tr>
              </thead>
              <tbody>
              <tr v-for="object in project.open_tasks" v-if="project.open_tasks && project.open_tasks.length">
                <td>
                  <span v-if="object.scope">
                    <font-awesome-icon icon="shield-alt" size="lg" class="mr-1"/>
                    {{ object.scope.name }}
                  </span>
                </td>
                <td>
                  {{ object.start_date | formatDate }}
                </td>
                <td>
                  <span class="badge badge-info pl-3 pr-3" v-if="object.days_left >= 0">
                    {{ object.days_left }} {{ $t('system.days') }}
                  </span>
                  <span class="badge badge-danger pl-3 pr-3" v-if="object.days_left < 0">
                    {{ object.days_left }} {{ $t('system.days') }}
                  </span>
                </td>
                <td>
                  <RiskTaskModal :task="object" @reloadContent="loadOpenTasks" :tasks="project.open_tasks"></RiskTaskModal>
                  <b-button class="mr-2 small"
                            variant="light"
                            @click="showEditUsersModal(object)"
                            qid="delete-s-cancel-button"
                  >
                    {{ $t('tasks.re_assign_button') }}
                  </b-button>
                </td>
              </tr>
              <tr v-if="!loadingOpen && project.open_tasks && !project.open_tasks.length" qid="no-results-row" class="text-center">
                <td colspan="10">
                  <h5 class="mt-3">
                    {{ $t('system.no_results') }}
                  </h5>
                </td>
              </tr>
              </tbody>
            </table>

            <table class="table table-responsive-md" v-if="project.type && project.type == 'risk_assessment'">
              <thead>
                <tr>
                  <th scope="col">
                    {{ $t('projects.table.scope') }}
                  </th>
                  <th scope="col">
                    {{ $t('projects.table.content') }}
                  </th>
                  <th scope="col">
                    {{ $t('projects.table.title') }}
                  </th>
                  <th scope="col">
                    {{ $t('projects.table.received_date') }}
                  </th>
                  <th>
                    {{ $t('projects.table.days_left') }}
                  </th>
                  <th width="250"></th>
                </tr>
              </thead>
              <tbody>
              <tr v-for="object in project.open_tasks" v-if="project.open_tasks && project.open_tasks.length">
                <td>
                  <span v-if="object.scope">
                    <font-awesome-icon icon="shield-alt" size="lg" class="mr-1" v-if="object.scope.who_am_i == 'App\\Maintenance\\Assessment\\Models\\Assessment'"/>
                    <font-awesome-icon icon="desktop" size="lg" class="mr-1" v-if="object.scope.who_am_i == 'App\\System\\Models\\System'"/>
                    <font-awesome-icon icon="cubes" size="lg" class="mr-1" v-if="object.scope.who_am_i == 'App\\Activity\\Models\\Activity'"/>
                    <font-awesome-icon icon="cube" size="lg" class="mr-1" v-if="object.scope.who_am_i == 'App\\Process\\Models\\Process'"/>
                    <font-awesome-icon icon="file-signature" size="lg" class="mr-1" v-if="object.scope.who_am_i == 'App\\Maintenance\\ThirdParty\\Models\\ThirdParty'"/>
                    {{ object.scope.name }}
                  </span>
                </td>
                <td>
                  <span v-if="object.model">
                    <font-awesome-icon icon="desktop" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\System\\Models\\System'"/>
                    <font-awesome-icon icon="cubes" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\Activity\\Models\\Activity'"/>
                    <font-awesome-icon icon="cube" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\Process\\Models\\Process'"/>
                    <font-awesome-icon icon="file-signature" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\Maintenance\\ThirdParty\\Models\\ThirdParty'"/>
                    {{ object.model.name }}
                  </span>
                </td>
                <td>
                  <span v-if="object.code == 'business_risk_assessment' || object.code == 'privacy_risk_assessment' || object.code == 'system_impact_assessment'">
                    {{ $t('maintenance.assessments.system_impact_assessment_label') }}
                  </span>
                  <span v-else>{{ object.title}}</span>
                </td>
                <td>
                  {{ object.start_date | formatDate }}
                </td>
                <td>
                  <span class="badge badge-info pl-3 pr-3" v-if="object.days_left >= 0">
                    {{ object.days_left }} {{ $t('system.days') }}
                  </span>
                  <span class="badge badge-danger pl-3 pr-3" v-if="object.days_left < 0">
                    {{ object.days_left }} {{ $t('system.days') }}
                  </span>
                </td>
                <td>
                  <AssessmentTaskModal
                      :task="object"
                      :tasks="project.open_tasks"
                      @reloadContent="loadOpenTasks"
                  ></AssessmentTaskModal>
                  <b-button class="ml-2 small"
                            variant="light"
                            @click="showEditUsersModal(object)"
                            qid="delete-s-cancel-button"
                  >
                    {{ $t('tasks.re_assign_button') }}
                  </b-button>
                </td>
              </tr>
              <tr v-if="!loadingOpen && project.open_tasks && !project.open_tasks.length" qid="no-results-row" class="text-center">
                <td colspan="10">
                  <h5 class="mt-3">
                    {{ $t('system.no_results') }}
                  </h5>
                </td>
              </tr>
              </tbody>
            </table>

          </div>
        </div>
        <br>
        <div class="card" v-if="!loadingOpen && getMyProjects && !getMyProjects.length">
          <div class="card-body text-center">
            <h5 class="mt-2">{{ $t('system.no_results') }}</h5>
          </div>
        </div>
        <div class="mt-3 text-center" v-if="loadingOpen">
          <h1>
            <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
          </h1>
        </div>
      </b-tab>

      <!-- Closed tasks tab -->
      <b-tab :title="$t('projects.tab.closed_tasks')" @click="closedTasksTabClicked">
        <div v-for="project in myProjectsClosed" class="mt-4">
          <div class="row" v-if="project.closed_tasks && project.closed_tasks.length">
            <div class="col-sm-5">
              <h5>
                {{ project.title }}
              </h5>
            </div>
            <div class="col-sm-7 text-right">
              <span class="mr-2" v-if="project.end_date">
                {{ $t('projects.table.due_date') }}:
              </span>
              <span class="mr-4 badge badge-success">{{ project.end_date | formatDate }}</span>

              <span class="mr-2">{{ $t('projects.table.type') }}:</span>
              <span v-if="project.type == 'update'" class="badge badge-info mr-4">
                {{ $t('projects.view.update_label') }}
              </span>
              <span v-if="project.type == 'validation'" class="badge badge-info mr-4">
                {{ $t('projects.view.validation_label') }}
              </span>
              <span v-if="project.type == 'control'" class="badge badge-info mr-4">
                {{ $t('projects.view.control_label') }}
              </span>
              <span v-if="project.type == 'security_measure'" class="badge badge-info mr-4">
                {{ $t('projects.view.security_measure_label') }}
              </span>
              <span v-if="project.type == 'risk_assessment'" class="badge badge-info mr-4">
                  {{ $t('maintenance.assessments.type.' + $options.filters.slugify(project.assessment.type)) }}
                </span>

              <span v-if="project.type != 'security_measure' && project.type != 'risk_assessment'">
                <span class="mr-2">{{ $t('projects.table.scope') }}:</span>
                <span class="badge badge-info mr-4" v-if="project.scope == 'system'">
                  {{ $t('projects.system.label') }}
                </span>
                <span class="badge badge-info mr-4" v-if="project.scope == 'process'">
                  {{ $t('projects.process.label') }}
                </span>
                <span class="badge badge-info mr-4" v-if="project.scope == 'activity'">
                  {{ $t('projects.activity.label') }}
                </span>
                <span class="badge badge-info mr-4" v-if="project.scope == 'third_party'">
                  {{ $t('projects.third_party.label') }}
                </span>
                <span class="badge badge-info mr-4" v-if="project.type == 'risk_assessment'">
                      {{ project.assessment.name }}
                </span>
              </span>

              <span v-if="project.assessment && project.type == 'risk_assessment'">
                <span class="mr-2">
                  {{ $t('table.template') }}:
                </span>
                <span class="badge badge-info mr-4" v-if="project.type == 'risk_assessment'">
                  {{ project.assessment.name }}
                </span>
              </span>
            </div>
          </div>
          <div class="card" v-if="project.closed_tasks && project.closed_tasks.length">
            <table class="table table-responsive-md" v-if="project.type && (project.type == 'update' || project.type == 'validation')">
              <thead>
                <tr>
                  <th scope="col">
                    {{ $t('projects.table.content') }}
                  </th>
                  <th scope="col">
                    {{ $t('projects.table.name') }}
                  </th>
                  <th scope="col">
                    {{ $t('projects.table.received_date') }}
                  </th>
                  <th>
                    {{ $t('projects.table.closed_date') }}
                  </th>
                  <th>
                    {{ $t('projects.table.response_rate') }}
                  </th>
                  <th scope="col">
                    {{ $t('projects.table.close_type') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="object in project.closed_tasks" v-if="project.closed_tasks && project.closed_tasks.length">
                  <td>
                    <span v-if="object.model">
                      <font-awesome-icon icon="desktop" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\System\\Models\\System'"/>
                      <font-awesome-icon icon="cubes" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\Activity\\Models\\Activity'"/>
                      <font-awesome-icon icon="cube" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\Process\\Models\\Process'"/>
                      <font-awesome-icon icon="file-signature" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\Maintenance\\ThirdParty\\Models\\ThirdParty'"/>
                      {{ object.model.name }}
                    </span>
                  </td>
                  <td>
                    <span>
                      {{ $t('tasks.values.' + $options.filters.slugify(object.title)) }}
                    </span>
                  </td>
                  <td>
                    {{ object.start_date | formatDate }}
                  </td>
                  <td>
                    {{ object.completed_at | formatDate }}
                  </td>
                  <td>
                    <span class="badge badge-info pl-3 pr-3">
                      {{ object.response_rate}} {{ $t('system.days') }}
                    </span>
                  </td>
                  <td>
                    <span class="badge badge-success">
                      {{ $t('system.' + $options.filters.slugify(object.status)) }}
                    </span>
                  </td>
                </tr>
                <tr v-if="!loadingClosed && project.closed_tasks && !project.closed_tasks.length" qid="no-results-row" class="text-center">
                  <td colspan="10">
                    <h5 class="mt-3">
                      {{ $t('system.no_results') }}
                    </h5>
                  </td>
                </tr>
              </tbody>
            </table>
            <table class="table table-responsive-md" v-if="project.type && project.type == 'control'">
              <thead>
                <tr>
                  <th scope="col">
                    {{ $t('projects.table.scope') }}
                  </th>
                  <th scope="col">
                    {{ $t('projects.table.content') }}
                  </th>
                  <th scope="col">
                    {{ $t('projects.table.received_date') }}
                  </th>
                  <th>
                    {{ $t('projects.table.closed_date') }}
                  </th>
                  <th>
                    {{ $t('projects.table.response_rate') }}
                  </th>
                  <th scope="col">
                    {{ $t('projects.table.close_type') }}
                  </th>
                  <th width="150"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="object in project.closed_tasks" v-if="project.closed_tasks && project.closed_tasks.length">
                  <td>
                    <span v-if="object.scope">
                      <font-awesome-icon icon="shield-alt" size="lg" class="mr-1"/>
                      {{ object.scope.name }}
                    </span>
                  </td>
                  <td>
                    <span v-if="object.model">
                      <font-awesome-icon icon="desktop" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\System\\Models\\System'"/>
                      <font-awesome-icon icon="cubes" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\Activity\\Models\\Activity'"/>
                      <font-awesome-icon icon="cube" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\Process\\Models\\Process'"/>
                      <font-awesome-icon icon="file-signature" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\Maintenance\\ThirdParty\\Models\\ThirdParty'"/>
                      {{ object.model.name }}
                    </span>
                  </td>
                  <td>
                    {{ object.start_date | formatDate }}
                  </td>
                  <td>
                    {{ object.completed_at | formatDate }}
                  </td>
                  <td>
                    <span class="badge badge-info pl-3 pr-3">
                      {{ object.response_rate}} {{ $t('system.days') }}
                    </span>
                  </td>
                  <td>
                    <span class="badge badge-success">
                      {{ $t('system.' + $options.filters.slugify(object.status)) }}
                    </span>
                  </td>
                  <td>
                    <b-button class="mr-2"
                              variant="success"
                              @click="showViewTaskModal(object)"
                              qid="delete-s-cancel-button"
                    >
                      {{ $t('tasks.view_button') }}
                    </b-button>
                  </td>
                </tr>
                <tr v-if="!loadingClosed && project.closed_tasks && !project.closed_tasks.length" qid="no-results-row" class="text-center">
                  <td colspan="10">
                    <h5 class="mt-3">
                      {{ $t('system.no_results') }}
                    </h5>
                  </td>
                </tr>
              </tbody>
            </table>
            <table class="table table-responsive-md" v-if="project.type && project.type == 'security_measure'">
              <thead>
                <tr>
                  <th scope="col">
                    {{ $t('projects.table.scope') }}
                  </th>
                  <th scope="col">
                    {{ $t('projects.table.received_date') }}
                  </th>
                  <th>
                    {{ $t('projects.table.closed_date') }}
                  </th>
                  <th>
                    {{ $t('projects.table.response_rate') }}
                  </th>
                  <th scope="col">
                    {{ $t('projects.table.close_type') }}
                  </th>
                  <th width="150"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="object in project.closed_tasks" v-if="project.closed_tasks && project.closed_tasks.length">
                  <td>
                    <span v-if="object.scope">
                      <font-awesome-icon icon="shield-alt" size="lg" class="mr-1"/>
                      {{ object.scope.name }}
                    </span>
                  </td>
                  <td>
                    {{ object.start_date | formatDate }}
                  </td>
                  <td>
                    {{ object.completed_at | formatDate }}
                  </td>
                  <td>
                    <span class="badge badge-info pl-3 pr-3">
                    {{ object.response_rate}} {{ $t('system.days') }}
                    </span>
                  </td>
                  <td>
                    <span class="badge badge-success">
                      {{ object.status | capitalize }}
                    </span>
                  </td>
                  <td>
                    <b-button class="mr-2"
                              variant="success"
                              @click="showViewTaskModal(object)"
                              qid="delete-s-cancel-button"
                    >
                      {{ $t('tasks.view_button') }}
                    </b-button>
                  </td>
                </tr>
                <tr v-if="!loadingClosed && project.closed_tasks && !project.closed_tasks.length" qid="no-results-row" class="text-center">
                  <td colspan="10">
                    <h5 class="mt-3">
                      {{ $t('system.no_results') }}
                    </h5>
                  </td>
                </tr>
              </tbody>
            </table>
            <table class="table table-responsive-md" v-if="project.type && project.type == 'risk_assessment'">
              <thead>
              <tr>
                <th scope="col">
                  {{ $t('projects.table.scope') }}
                </th>
                <th scope="col">
                  {{ $t('projects.table.content') }}
                </th>
                <th scope="col">
                  {{ $t('projects.table.name') }}
                </th>
                <th scope="col">
                  {{ $t('projects.table.received_date') }}
                </th>
                <th>
                  {{ $t('projects.table.closed_date') }}
                </th>
                <th>
                  {{ $t('projects.table.response_rate') }}
                </th>
                <th scope="col">
                  {{ $t('projects.table.close_type') }}
                </th>
                <th width="150"></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="object in project.closed_tasks" v-if="project.closed_tasks && project.closed_tasks.length">
                <td>
                    <span v-if="object.scope">
                      <font-awesome-icon icon="shield-alt" size="lg" class="mr-1" v-if="object.scope.who_am_i == 'App\\Maintenance\\Assessment\\Models\\Assessment'"/>
                      <font-awesome-icon icon="desktop" size="lg" class="mr-1" v-if="object.scope.who_am_i == 'App\\System\\Models\\System'"/>
                      <font-awesome-icon icon="cubes" size="lg" class="mr-1" v-if="object.scope.who_am_i == 'App\\Activity\\Models\\Activity'"/>
                      <font-awesome-icon icon="cube" size="lg" class="mr-1" v-if="object.scope.who_am_i == 'App\\Process\\Models\\Process'"/>
                      <font-awesome-icon icon="file-signature" size="lg" class="mr-1" v-if="object.scope.who_am_i == 'App\\Maintenance\\ThirdParty\\Models\\ThirdParty'"/>
                      {{ object.scope.name }}
                    </span>
                </td>
                <td>
                    <span v-if="object.model">
                      <font-awesome-icon icon="desktop" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\System\\Models\\System'"/>
                      <font-awesome-icon icon="cubes" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\Activity\\Models\\Activity'"/>
                      <font-awesome-icon icon="cube" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\Process\\Models\\Process'"/>
                      <font-awesome-icon icon="file-signature" size="lg" class="mr-1" v-if="object.model.who_am_i == 'App\\Maintenance\\ThirdParty\\Models\\ThirdParty'"/>
                      {{ object.model.name }}
                    </span>
                </td>
                <td>
                  <span v-if="object.code == 'business_risk_assessment' || object.code == 'privacy_risk_assessment' || object.code == 'system_impact_assessment'">{{ $t('maintenance.assessments.system_impact_assessment_label') }}</span>
                  <span v-else>{{ object.title}}</span>
                </td>
                <td>
                  {{ object.start_date | formatDate }}
                </td>
                <td>
                  {{ object.completed_at | formatDate }}
                </td>
                <td>
                  <span class="badge badge-info pl-3 pr-3">
                    {{ object.response_rate}} {{ $t('system.days') }}
                  </span>
                </td>
                <td>
                  <span class="badge badge-success">
                    {{ $t('system.' + $options.filters.slugify(object.status)) }}
                  </span>
                </td>
                <td>
                  <b-button class="mr-2"
                            variant="success"
                            @click="showViewTaskModal(object)"
                            qid="delete-s-cancel-button"
                  >
                    {{ $t('tasks.view_button') }}
                  </b-button>
                </td>
              </tr>
              <tr v-if="!loadingOpen && project.open_tasks && !project.open_tasks.length" qid="no-results-row" class="text-center">
                <td colspan="10">
                  <h5 class="mt-3">
                    {{ $t('system.no_results') }}
                  </h5>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
        <br>
        <div class="card" v-if="!loadingClosed && getMyProjectsClosed && !getMyProjectsClosed.length">
          <div class="card-body text-center">
            <h5 class="mt-2">{{ $t('system.no_results') }}</h5>
          </div>
        </div>
        <div class="mt-3 text-center" v-if="loadingClosed">
          <h1>
            <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
          </h1>
        </div>
      </b-tab>

    </b-tabs>


    <!-- Re-assign modal -->
    <b-modal ref="re-assign" hide-footer @hide="hideEditUsersModal">
      <div class="d-block text-center">
        <h4 qid="update-tp-title">
          {{ $t('tasks.re_assign.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="create-p-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="reassignFormSubmit" qid="create-p-form" v-if="selectedTask && selectedTask.model">
        <div class="form-container">
          <b-form-group>
            <label qid="create-p-users-label">
              {{ $t('projects.create.owners_label') }}
            </label>
            <multiselect
                v-model="selectedAssignUsers"
                :options="allUsers"
                :multiple="true"
                :taggable="false"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                track-by="slug"
                :placeholder="$t('projects.create.owners_placeholder')"
                label="name"
                qid="create-p-users-select"
            >
              <template slot="tag" slot-scope="props">
                <span class="mr-3 user-tag">
                  <Avatar :object="props.option" size="xs"></Avatar>
                  <span class="ml-2">{{ props.option.name }}</span>
                </span>
              </template>
              <template slot="option" slot-scope="props">
                <Avatar :object="props.option" size="xs"></Avatar>
                <span class="ml-2">
                  {{ props.option.name }}
                </span>
              </template>
            </multiselect>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditUsersModal"
                    qid="create-p-cancel-button"
          >
            {{ $t('projects.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="create-p-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('projects.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>


    <TaskViewModal
        :selectedTask="selectedTask"
        :showModal="viewTaskModal"
        @closeViewModal="hideViewTaskModal"
    ></TaskViewModal>

    <ShowMoreModal
        :items="selectedItem.users"
        :show="showUsersModal"
        @closeViewModal="closeUsersModal"
        type="users"
    ></ShowMoreModal>

  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import ProcessEditForm from '@/views/Processes/ProcessEditForm.vue';
  import {config} from '@/config.js';
  import Avatar from '@/components/Avatar.vue';
  import ShowMoreModal from '@/components/ShowMoreModal.vue';
  import AssessmentTaskModal from '@/views/Tasks/AssessmentTaskModal.vue';
  import RiskTaskModal from '@/views/Tasks/RiskTaskModal.vue';
  import UpdateTaskModal from '@/views/Tasks/UpdateTaskModal.vue';
  import ValidationTaskModal from '@/views/Tasks/ValidationTaskModal.vue';
  import Datepicker from 'vuejs-datepicker';
  import TaskViewModal from '@/views/Tasks/TaskViewModal.vue';

  export default {
    name: 'Tasks',
    data: function() {
      return {
        breadcrumbItems: [
          {
            text: this.$t('dashboard.title'),
            to: '/'
          },
          {
            text: this.$t('tasks.title'),
            active: true
          }
        ],
        taskForm: {
          remarks: ''
        },
        itemToDelete: {},
        descriptionLength: config.descriptionLength,
        showFullProjectMessage: false,
        showFullGenericControlDescription: false,
        selectedSort: '',
        selectedDirection: '',
        keywordValue: '',
        projectTypes: [
          { name: 'Update', value: 'update' },
          { name: 'Validation', value: 'validation' }
        ],
        projectScopes: [
          { name: 'Process', value: 'process' },
          { name: 'Activity', value: 'activity' },
          { name: 'System', value: 'system' },
          { name: 'Third party', value: 'third_party' }
        ],
        loadingOpen: true,
        loadingClosed: true,

        // Pagination
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],
        perPage: '',
        selectedPerPage: "10",
        currentPage: 1,
        totalRows: 0,
        users: [],
        selectedUsers: [],
        statistics: {},

        selectedItem: {},
        showUsersModal: false,
        showOpenTasksTab: true,
        showClosedTasksTab: false,
        myProjectsOpen: [],
        myProjectsClosed: [],
        error: '',
        selectedTask: [],
        selectedAssignUsers: [],
        buttonDisabled: false,
        top_percentage: 0,
        topUserListOpened: false,
        topUsers: {},
        organization_percentage: 0,
        topOrganizationListOpened: false,
        topOrganizations: {},
        allUsers: [],
        viewTaskModal: false,
      }
    },
    components: {
      ProcessEditForm,
      Avatar,
      Datepicker,
      ShowMoreModal,
      AssessmentTaskModal,
      RiskTaskModal,
      UpdateTaskModal,
      TaskViewModal,
      ValidationTaskModal
    },
    computed: {
      getUser() {
        return this.$store.state.user
      },
      getClient() {
        return this.$store.state.client
      },
      getMyProjects() {
        return this.myProjectsOpen
      },
      getMyProjectsClosed() {
        return this.myProjectsClosed
      },
      getReloadPage() {
        return this.$store.state.updatePage
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadOpenTasks()

        if (this.showClosedTasksTab) {
          this.loadClosedTasks()
        }

      },
      getReloadPage: function () {
        this.loadOpenTasks()
      }
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadOpenTasks()
      }, 400),
      openUsersModal(object) {
        this.selectedItem = object
        this.showUsersModal = true
      },
      closeUsersModal() {
        this.selectedItem = {}
        this.showUsersModal = false
      },
      getReducedUsers(users) {
        let reducedUsers = []
        if (users && users.length > 3) {
          reducedUsers = _.cloneDeep(users);
          reducedUsers = reducedUsers.slice(0, 3);
        } else {
          reducedUsers = users;
        }
        return reducedUsers;
      },
      loadTaskById(id) {
        let self = this;
        piincHttp.get('tasks/' + id).then(function(response) {
          self.selectedTask = response.data;
        }, function() {});
      },
      downloadMedia(object){
        piincHttp.get('documents/' + object.id + '/download', {responseType: 'blob'}).then(function(response) {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.download = object.file_name;

          link.click();
          link.remove()
          window.URL.revokeObjectURL(url);
        }, function() {});
      },
      showViewTaskModal(object) {
        this.selectedTask = object
        this.viewTaskModal = true
      },
      hideViewTaskModal() {
        this.selectedTask = {}
        this.viewTaskModal = false
      },
      hideEditUsersModal() {
        this.selectedTask = {}
        this.selectedAssignUsers = []
        this.$refs['re-assign'].hide()
      },
      showEditUsersModal(object) {
        let self = this;
        this.error = '';

        let role_name = [self.permissions.ROLE_CLIENT_ADMIN];

        if (object.project.type == 'control' || object.project.type == 'security_measure') {
          role_name.push(self.permissions.ROLE_MEMBER);
          role_name.push(self.permissions.ROLE_VALIDATOR);
        }

        if (this.getClient && this.getClient.parent) {
          role_name.push(self.permissions.ROLE_PARTITION_ADMIN);
        }

        this.selectedAssignUsers = [];
        piincHttp.get('users', { params:
          {
            client: this.getClient.id,
            role_name: role_name,
            per_page: 100
          }
        }).then(function(response) {
          let users = response.data.items
          _.forEach(object.users, function (user) {
            if (_.findIndex(users, function(o) { return o.id == user.id; }) == -1) {
              if (user.id != self.getUser.id) {
                users.push(user)
              }
            }
            if (user.id != self.getUser.id) {
              let matchedUser = _.cloneDeep(user)
              if (user.id == object.owner) {
                matchedUser.owner = true
              }
              self.selectedAssignUsers.push(matchedUser)
            }
          })
          self.allUsers = _.uniq(users)
        }, function() {});

        this.selectedTask = object
        this.$refs['re-assign'].show()
      },
      reassignFormSubmit(evt) {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        let userIds = [];
        evt.preventDefault();

        if (this.selectedAssignUsers && this.selectedAssignUsers.length) {
          for (var i = 0; i < this.selectedAssignUsers.length; i++) {
            userIds.push(this.selectedAssignUsers[i].id);
          }
        }

        if (userIds && userIds.length) {
          piincHttp.put('tasks/' + this.selectedTask.id, {
            users: userIds,
            reasign: 'replace'
          }).then(function(response) {
            self.hideEditUsersModal();
            self.loadOpenTasks()
            self.$toastr('success', self.$t('tasks.re_assign.success'))
            self.buttonDisabled = false;
          }, function(error) {
            if (error.status !== 200) {
              self.buttonDisabled = false;
              self.error = error.data.message;
            }
          })
        } else {
          self.error = self.$t('tasks.re_assign.required')
          self.buttonDisabled = false;
        }
      },
      loadOpenTasks() {
        let self = this;
        this.myProjectsOpen = []
        this.loadingOpen = true;
        piincHttp.get('projects/me', { params:
          {
            sort: this.selectedSort !== '' ? this.selectedSort : undefined,
            order: this.selectedDirection !== '' ? this.selectedDirection : undefined,
            keyword: this.keywordValue !== '' ? this.keywordValue : undefined,
            per_page: 100,
            status: 'in progress',
            client: this.getClient.slug
          }
        }).then(function(response) {
          let myProjects = response.data.items;

          if (!myProjects.length) {
            self.loadingOpen = false;
          }

          _.forEach(myProjects, function (item, key) {
            self.loadingOpen = true;
            // Call API for open tasks
            piincHttp.get('projects/' + item.slug + '/tasks/me', { params:
              {
                status: 'pending',
                per_page: 100
              }
            }).then(function(response) {
              item.open_tasks = response.data.items;
              self.myProjectsOpen.splice(key, 0, item)
              self.loadingOpen = false;
            }, function() {});

          })
        }, function() {});
      },
      loadClosedTasks() {
        let self = this;
        this.myProjectsClosed = []
        this.loadingClosed = true;
        piincHttp.get('projects/me', { params:
          {
            sort: this.selectedSort !== '' ? this.selectedSort : undefined,
            order: this.selectedDirection !== '' ? this.selectedDirection : undefined,
            keyword: this.keywordValue !== '' ? this.keywordValue : undefined,
            per_page: 100,
            client: this.getClient.slug
          }
        }).then(function(response) {
          let myProjects = response.data;

          if (!myProjects.length) {
            self.loadingClosed = false;
          }

          _.forEach(myProjects, function (item, key) {
            self.loadingClosed = true;

            // Call API for closed tasks
            piincHttp.get('projects/' + item.slug + '/tasks/me', { params:
              {
                status: 'completed',
                per_page: 100
              }
            }).then(function(response) {
              self.loadingClosed = false;
              self.myProjectsClosed.splice(key, 0, item)
              item.closed_tasks = response.data.items;
            }, function() {});
          })
        }, function() {});
      },
      loadUsers(query) {
        let self = this;
        piincHttp.get('users', { params:
          {
            client: this.getClient.id,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.users = response.data.items
        }, function() {});
      },
      openTasksTabClicked() {
        this.loadOpenTasks()
        this.showClosedTasksTab = false
        this.showOpenTasksTab = true
      },
      closedTasksTabClicked() {
        this.loadClosedTasks()
        this.showClosedTasksTab = true;
        this.showOpenTasksTab = false
      },
      navigateToTask(object) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', object);

        if (object.model_type == 'App\\Activity\\Models\\Activity') {
          this.$router.push({ name: 'activities.view', params: { slug: object.model.slug }, query: { task: object.id }})
        }
        if (object.model_type == 'App\\Process\\Models\\Process') {
          this.$router.push({ name: 'processes.view', params: { slug: object.model.slug }, query: { task: object.id }})
        }
        if (object.model_type == 'App\\System\\Models\\System') {
          this.$router.push({ name: 'systems.view', params: { slug: object.model.slug }, query: { task: object.id }})
        }
        if (object.model_type == 'App\\Maintenance\\ThirdParty\\Models\\ThirdParty') {
          this.$router.push({ name: 'contracts.view', params: { slug: object.model.slug }, query: { task: object.id }})
        }
      },
    },
    created () {
      if (this.getClient && this.getClient.slug) {
        this.loadOpenTasks()
      }
    }
  }
</script>
